// The Flows builder's API — mirrors packages/server/src/routes/flows.ts and
// routes/contact-attributes.ts.

import type { FlowGraph, FlowIssue, FlowStep, HttpRequestSpec } from '@prakash-software/shared';
import { apiFetch } from './api';

export type FlowTriggerKind = 'conversation_started' | 'message_received' | 'manual';
export type FlowRunStatus = 'running' | 'waiting' | 'completed' | 'failed' | 'cancelled';

export interface FlowSummary {
  id: string;
  name: string;
  description: string | null;
  enabled: boolean;
  triggerKind: FlowTriggerKind | null;
  publishedVersion: number | null;
  publishedAt: string | null;
  updatedAt: string;
  nodeCount: number;
  unpublishedChanges: boolean;
  runs30d: { total: number; live: number; failed: number; lastRunAt: string | null };
}

export interface FlowTemplateSummary {
  id: string;
  name: string;
  description: string;
  nodeCount: number;
  triggerKind: FlowTriggerKind | null;
}

export interface FlowValidationResult {
  ok: boolean;
  errors: FlowIssue[];
  warnings: FlowIssue[];
}

export interface FlowDetail {
  flow: {
    id: string;
    siteId: string;
    name: string;
    description: string | null;
    enabled: boolean;
    triggerKind: FlowTriggerKind | null;
    publishedVersionId: string | null;
    updatedAt: string;
    unpublishedChanges: boolean;
  };
  graph: FlowGraph;
  validation: FlowValidationResult;
  versions: Array<{ id: string; version: number; publishedAt: string; publishedBy: string | null }>;
  agents: Array<{ id: string; name: string }>;
}

export interface FlowRunSummary {
  id: string;
  status: FlowRunStatus;
  sessionId: string | null;
  isTest: boolean;
  version: number;
  error: string | null;
  startedAt: string;
  finishedAt: string | null;
}

export interface FlowRunDetail {
  run: Omit<FlowRunSummary, 'isTest'> & { graph: FlowGraph; state: { vars: Record<string, unknown> } };
  steps: Array<Pick<FlowStep, 'nodeId' | 'nodeType' | 'outcome' | 'detail'> & { seq: number; at: string }>;
}

const base = (siteId: string) => `/api/admin/sites/${encodeURIComponent(siteId)}/flows`;
const one = (siteId: string, flowId: string) => `${base(siteId)}/${encodeURIComponent(flowId)}`;

export function listFlows(siteId: string): Promise<{ flows: FlowSummary[] }> {
  return apiFetch(base(siteId));
}

export function listFlowTemplates(siteId: string): Promise<{ templates: FlowTemplateSummary[] }> {
  return apiFetch(`${base(siteId)}/templates`);
}

export function createFlow(siteId: string, body: { name: string; templateId?: string | null }): Promise<{ id: string }> {
  return apiFetch(base(siteId), { method: 'POST', body: JSON.stringify(body) });
}

export function getFlow(siteId: string, flowId: string): Promise<FlowDetail> {
  return apiFetch(one(siteId, flowId));
}

export function saveFlow(
  siteId: string,
  flowId: string,
  body: { name?: string; description?: string | null; graph?: FlowGraph },
): Promise<{ ok: true; validation?: FlowValidationResult }> {
  return apiFetch(one(siteId, flowId), { method: 'PUT', body: JSON.stringify(body) });
}

export function publishFlow(siteId: string, flowId: string): Promise<{ versionId: string; version: number; warnings: FlowIssue[] }> {
  return apiFetch(`${one(siteId, flowId)}/publish`, { method: 'POST' });
}

export function setFlowEnabled(siteId: string, flowId: string, enabled: boolean): Promise<{ ok: true }> {
  return apiFetch(`${one(siteId, flowId)}/enable`, { method: 'POST', body: JSON.stringify({ enabled }) });
}

export function restoreFlowVersion(siteId: string, flowId: string, versionId: string): Promise<{ ok: true; graph: FlowGraph }> {
  return apiFetch(`${one(siteId, flowId)}/restore`, { method: 'POST', body: JSON.stringify({ versionId }) });
}

export function deleteFlow(siteId: string, flowId: string): Promise<void> {
  return apiFetch(one(siteId, flowId), { method: 'DELETE' });
}

export function listFlowRuns(siteId: string, flowId: string, status?: FlowRunStatus): Promise<{ runs: FlowRunSummary[] }> {
  return apiFetch(`${one(siteId, flowId)}/runs${status ? `?status=${status}` : ''}`);
}

export function getFlowRun(siteId: string, flowId: string, runId: string): Promise<FlowRunDetail> {
  return apiFetch(`${one(siteId, flowId)}/runs/${encodeURIComponent(runId)}`);
}

export function testFlowHttp(
  siteId: string,
  flowId: string,
  request: HttpRequestSpec,
): Promise<{ status: number; body?: unknown; error?: string }> {
  return apiFetch(`${one(siteId, flowId)}/test-http`, { method: 'POST', body: JSON.stringify(request) });
}

export function getContactAttributes(siteId: string, contactId: string): Promise<{ attributes: Record<string, string> }> {
  return apiFetch(`/api/admin/sites/${encodeURIComponent(siteId)}/contacts/${encodeURIComponent(contactId)}/attributes`);
}

export function saveContactAttributes(
  siteId: string,
  contactId: string,
  attributes: Record<string, string>,
): Promise<{ attributes: Record<string, string> }> {
  return apiFetch(`/api/admin/sites/${encodeURIComponent(siteId)}/contacts/${encodeURIComponent(contactId)}/attributes`, {
    method: 'PUT',
    body: JSON.stringify({ attributes }),
  });
}
